import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionType } from '../../../generated/hooks';
import { ColumnType } from '../components/AnswerGroupTable';
import type { MetricCellPresentation, TableColumn } from '../components/AnswerGroupTable/types';
import type { NewReport } from '../hooks/useNewReport';
import type { ReportQuestion, TextSource } from '../types';
import { localizedText, type ReportTableColumn } from '../definition/types';
import { useDefinition } from '../definition/ReportDefinitionContext';
import { GROUP_COLUMNS, getTextSources } from './tableTestColumns';
import {
  Action,
  Composer,
  Disclosure,
  Field,
  Hint,
  NumberGrid,
  PanelHeader,
  Segment,
  Segments,
  Stack,
  Tile,
  TileGrid,
} from './tablePlaygroundStyles';

type Sizing = Pick<TableColumn, 'width' | 'minWidth' | 'maxWidth'>;

type Props = {
  report: NewReport;
  questions: ReportQuestion[];
  commentQuestions: Set<string>;
  editingColumn?: TableColumn;
  language?: string;
  onAdd: (column: ReportTableColumn) => void;
  onClose: () => void;
};

/** Question/data first, presentation second. This composer edits exactly one column, never table properties. */
export function TableColumnComposer({
  report,
  questions,
  commentQuestions,
  editingColumn,
  language,
  onAdd,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation('newReport');
  const { document, metricErrors } = useDefinition();
  const lang = language ?? i18n.language;
  const [category, setCategory] = useState<'group' | 'question' | 'metric'>(
    editingColumn && 'metricId' in editingColumn
      ? 'metric'
      : editingColumn && 'questionIndex' in editingColumn
        ? 'question'
        : 'group',
  );
  const [questionIndex, setQuestionIndex] = useState(
    editingColumn && 'questionIndex' in editingColumn ? editingColumn.questionIndex : '',
  );
  const [metricId, setMetricId] = useState(editingColumn && 'metricId' in editingColumn ? editingColumn.metricId : '');
  const [preferredType, setType] = useState(editingColumn?.type);
  const [preferredView, setView] = useState<MetricCellPresentation | undefined>(
    editingColumn?.type === ColumnType.CUSTOM_METRIC ? editingColumn.presentation : undefined,
  );
  const [statement, setStatement] = useState(
    editingColumn && 'statementIndex' in editingColumn ? String(editingColumn.statementIndex ?? '') : '',
  );
  const [preferredSource, setSource] = useState<TextSource>(
    editingColumn && 'source' in editingColumn ? editingColumn.source ?? 'text' : 'text',
  );
  const [label, setLabel] = useState(
    (editingColumn as ReportTableColumn | undefined)?.translations?.[lang] ?? editingColumn?.label ?? '',
  );
  const [sizing, setSizing] = useState<Sizing>({
    width: editingColumn?.width,
    minWidth: editingColumn?.minWidth,
    maxWidth: editingColumn?.maxWidth,
  });
  const question = questions.find((item) => item.index === questionIndex);
  const metric = document.metrics.find((item) => item.id === metricId);
  const sources = getTextSources(question, commentQuestions.has(questionIndex));
  const source = sources.includes(preferredSource) ? preferredSource : sources[0];
  const types: ColumnType[] = question
    ? [
        ColumnType.QUESTION_ANSWER_COUNT,
        ...(question.type !== QuestionType.Freetext ? [ColumnType.CHOICES] : []),
        ...(sources.length ? [ColumnType.TEXT, ColumnType.THEMES] : []),
      ]
    : [];
  const type =
    preferredType && types.includes(preferredType)
      ? preferredType
      : question?.type === QuestionType.Freetext
        ? ColumnType.TEXT
        : ColumnType.CHOICES;
  const views: MetricCellPresentation[] =
    metric?.calculation.type === 'nps'
      ? ['nps', 'miniNps', 'number']
      : metric?.calculation.type === 'scaleBands' || metric?.calculation.type === 'respondentSegment'
        ? ['distribution', 'percentage', 'number']
        : ['score', 'percentage', 'number'];
  const view =
    preferredView && views.includes(preferredView)
      ? preferredView
      : metric?.calculation.type === 'choiceMatchRate' || metric?.presentation.format.endsWith('Percent')
        ? 'percentage'
        : views[0];
  const base = {
    id: editingColumn?.id ?? 'new',
    label,
    translations: { ...(editingColumn as ReportTableColumn | undefined)?.translations, [lang]: label },
    ...sizing,
  };
  const submitQuestion = () => {
    if (!question) return;
    const binding = { questionIndex, statementIndex: statement === '' ? undefined : Number(statement) };
    if (type === ColumnType.TEXT || type === ColumnType.THEMES) onAdd({ ...base, ...binding, type, source });
    else if (type === ColumnType.CHOICES || type === ColumnType.QUESTION_ANSWER_COUNT)
      onAdd({ ...base, ...binding, type });
  };
  return (
    <Composer>
      <PanelHeader>
        <h3>{t(editingColumn ? 'definition.editColumn' : 'tableTest.addColumn')}</h3>
        <Action type="button" onClick={onClose}>
          ×
        </Action>
      </PanelHeader>
      <Segments>
        {(['group', 'question', 'metric'] as const).map((value) => (
          <Segment key={value} type="button" active={category === value} onClick={() => setCategory(value)}>
            {t(value === 'metric' ? 'definition.metric' : `tableTest.categories.${value}`)}
          </Segment>
        ))}
      </Segments>
      {category === 'group' ? (
        <>
          <Hint>{t('tableTest.groupColumnsHint')}</Hint>
          <TileGrid>
            {GROUP_COLUMNS.map((columnType) => (
              <Tile
                key={columnType}
                type="button"
                onClick={() =>
                  onAdd({
                    ...base,
                    type: columnType as
                      | ColumnType.LABEL
                      | ColumnType.TEAM
                      | ColumnType.RESPONDENT_COUNT
                      | ColumnType.ANSWER_COUNT
                      | ColumnType.RESPONSE_RATE,
                  })
                }
              >
                <strong>+ {t(`table.columns.${columnType}`)}</strong>
                <span>{t(`tableTest.columnHints.${columnType}`)}</span>
              </Tile>
            ))}
          </TileGrid>
        </>
      ) : category === 'question' ? (
        <Stack>
          <Field>
            {t('tableTest.chooseQuestionStep')}
            <select
              value={questionIndex}
              onChange={(event) => {
                setQuestionIndex(event.target.value);
                setType(undefined);
                setStatement('');
              }}
            >
              <option value="">{t('tableTest.chooseQuestion')}</option>
              {questions.map((item) => (
                <option key={item.index} value={item.index}>
                  {t('tableTest.questionOption', { type: t(`tableTest.questionTypes.${item.type}`), text: item.text })}
                </option>
              ))}
            </select>
          </Field>
          {question ? (
            <>
              <Field as="div">{t('tableTest.chooseDataStep')}</Field>
              <TileGrid>
                {types.map((columnType) => (
                  <Tile key={columnType} type="button" active={type === columnType} onClick={() => setType(columnType)}>
                    <strong>{t(`table.columns.${columnType}`)}</strong>
                    <span>
                      {t(
                        columnType === ColumnType.QUESTION_ANSWER_COUNT
                          ? 'definition.questionResponsesHelp'
                          : `tableTest.columnHints.${columnType}`,
                      )}
                    </span>
                  </Tile>
                ))}
              </TileGrid>
              {!!question.statementsLocal?.length && (
                <Field>
                  {t('tableTest.statement')}
                  <select value={statement} onChange={(event) => setStatement(event.target.value)}>
                    <option value="">{t('tableTest.wholeQuestion')}</option>
                    {question.statementsLocal.map((text, index) => (
                      <option key={index} value={index}>
                        {text}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {(type === ColumnType.TEXT || type === ColumnType.THEMES) && sources.length > 1 && (
                <Field>
                  {t('tableTest.textSource')}
                  <select value={source} onChange={(event) => setSource(event.target.value as TextSource)}>
                    {sources.map((value) => (
                      <option key={value} value={value}>
                        {t(value === 'text' ? 'tableTest.freeText' : 'tableTest.comments')}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              <Field>
                {t('definition.optionalColumnLabel')}
                <input value={label} onChange={(event) => setLabel(event.target.value)} />
              </Field>
              <Action primary type="button" onClick={submitQuestion}>
                {t(editingColumn ? 'tableTest.saveValues' : 'tableTest.addTypedColumn', {
                  type: t(`table.columns.${type}`),
                })}
              </Action>
            </>
          ) : (
            <Hint>{t('tableTest.questionColumnsHint')}</Hint>
          )}
        </Stack>
      ) : (
        <Stack>
          <Field>
            {t('definition.chooseMetric')}
            <select
              value={metricId}
              onChange={(event) => {
                setMetricId(event.target.value);
                setView(undefined);
              }}
            >
              <option value="">{t('definition.chooseMetric')}</option>
              {document.metrics.map((item) => (
                <option key={item.id} value={item.id} disabled={!!metricErrors[item.id]}>
                  {localizedText(item.title, lang)}
                  {metricErrors[item.id] ? ` (${t('definition.needsSetup')})` : ''}
                </option>
              ))}
            </select>
          </Field>
          {metric ? (
            <>
              <Field as="div">{t('definition.metricCellAppearance')}</Field>
              <TileGrid>
                {views.map((value) => (
                  <Tile key={value} active={view === value} type="button" onClick={() => setView(value)}>
                    <strong>{t(`definition.cellViews.${value}`)}</strong>
                    <span>{t(`definition.cellHints.${value}`)}</span>
                  </Tile>
                ))}
              </TileGrid>
              <Field>
                {t('definition.optionalColumnLabel')}
                <input value={label} onChange={(event) => setLabel(event.target.value)} />
              </Field>
              <Action
                primary
                type="button"
                disabled={!!metricErrors[metricId]}
                onClick={() => onAdd({ ...base, type: ColumnType.CUSTOM_METRIC, metricId, presentation: view })}
              >
                {t(editingColumn ? 'tableTest.saveValues' : 'definition.addMetricColumn')}
              </Action>
            </>
          ) : (
            <Hint>{t('definition.playgroundMetrics')}</Hint>
          )}
        </Stack>
      )}
      <Disclosure>
        <summary>{t('tableTest.columnSizing')}</summary>
        <Stack>
          <Hint>{t('tableTest.columnSizingHint')}</Hint>
          <NumberGrid>
            {(['width', 'minWidth', 'maxWidth'] as const).map((property) => (
              <Field key={property}>
                {t(`tableTest.${property}`)}
                <input
                  type="number"
                  min={1}
                  placeholder={t('tableTest.auto')}
                  value={sizing[property] ?? ''}
                  onChange={(event) =>
                    setSizing((previous) => ({
                      ...previous,
                      [property]: event.target.value === '' ? undefined : Number(event.target.value),
                    }))
                  }
                />
              </Field>
            ))}
          </NumberGrid>
        </Stack>
      </Disclosure>
    </Composer>
  );
}
